import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image";
        image: string;
        imageOverlay: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        imgbuttonTopic: string;
        buttonLink: LinkFieldValue;
        customCSS: string;
        buttonType: string;
    };
}
const enum fields {
    height = "height",
    heading = "heading",
    description = "description",
    slideIndicator = "slideIndicator",
    slides = "slides",
    buttonLink = "buttonLink",
    customCSS = "customCSS",
    buttonType = "buttonType",
}
const enum buttonType {
    masterfluxButton = "masterfluxButton",
    tecumsehButton = "tecumsehButton",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.description]: string;
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.buttonLink]: LinkFieldValue;
        [fields.slideIndicator]: boolean;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        [fields.buttonType]: buttonType;
    };
    extendedStyles?: SlideshowStyles;
}
export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    masterfluxButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    h2wrapper?: InjectableCss;
    imgwrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    contentDescription?: InjectableCss;
    headingWrapper?: TypographyProps;
    linkSlidebutton?: LinkPresentationProps;
    technologyWrapper?: InjectableCss;
    headingTitle?: TypographyProps;
    description?: InjectableCss;
}
export const slideshowStyles: SlideshowStyles = {
    technologyWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 31px;
            padding-bottom: 0;
        `,
    },
    linkSlidebutton: {
        css: css`
            padding: 10px 8px;
        `,
    },
    headingTitle: {
        variant: "h2",
        css: css`
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: center;
            text-transform: uppercase;
        `,
    },
    description: {
        css: css`
            text-align: center;
            /* color: ${COLORS.secondaryNavyOne}; */
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.montserrat};
            font-weight: ${TYPOGRAPHY.weight.base};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            text-align: center;
            line-height: 22px;
            p {
                font-size: ${TYPOGRAPHY.size.mbase} !important;
            }
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
            padding: 31px;
            border-radius: 7px;
            opacity: 1;
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
            max-width: 913px;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: ${COLORS.white};
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            position: absolute;
            top: 30%;
            gap: 10px;
            margin: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 10%;
                        left: 4%;
                    `,
                    css`
                        top: 10%;
                        left: 2%;
                    `,
                    css`
                        top: 10%;
                        left: 2%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    dotButton: {
        css: css`
            height: 3px;
            padding: 13px 10px;
            padding-right: 30px;
            margin: 0 4px;
            border: 0;
            border-radius: 0 25px 25px 0;
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            border: 0.25px solid ${getColor("secondary")} !important;
            color: ${getColor("primary")};
            border-radius: 0px 20px 20px 0px;
            width: 300px;
            opacity: 1;
            background-color: ${COLORS.background.base};
            position: relative;
            text-align: left;
            text-transform: uppercase;
            span {
                font-size: ${TYPOGRAPHY.size.small} !important;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                &:hover {
                    color: ${getColor("secondary")};
                    font-size: ${TYPOGRAPHY.size.base} !important;
                }
            }
            &:hover,
            &:active {
                background-color: ${COLORS.background.base};
                border: 0.25px solid ${COLORS.secondaryNavyOne} !important;
                &:after {
                    background-color: ${COLORS.yellow};
                }
            }
            &:focus {
                outline: none;
            }
            &:after {
                content: "";
                width: 10px;
                margin-left: 10px;
                height: 10px;
                position: absolute;
                right: 9px;
                top: 15px;
                border-radius: 13px;
                border: 0.25px solid rgba(17, 41, 69, 0.219214);
                box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.638631) inset;
                opacity: 1;
                background-color: ${COLORS.background.whiteSmoke};
                display: inline-block;
                color: ${COLORS.black};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        span {
                            font-size: ${TYPOGRAPHY.size.smaller} !important;
                        }
                    `,
                    css``,
                    css``,
                    css`
                        width: 250px;
                    `,
                    null,
                ])}
        `,
    },
    h2wrapper: {
        css: css`
            display: flex;
            width: auto;
            border: 1px solid ${COLORS.normalGray};
            border-radius: 0 25px 25px 0;
            background: linear-gradient(to bottom, #ffffff 55%, #ffffff 100%);
            font-size: 14px;
            &:active {
                &:after {
                    background-color: ${COLORS.background.yellow};
                }
            }
            span {
                color: ${COLORS.black} !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                padding: 0 6px;
                opacity: 1;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.vSmaller};
                text-align: left;
            }
            &::after {
                content: "";
                width: 11px;
                margin-left: 10px !important;
                height: 11px;
                border-radius: 50%;
                display: inline-block;
                position: relative;
                margin: auto;
                left: -12px;
                border-radius: 5.499547px;
                border: 0.25px solid rgba(17, 41, 69, 0.219214);
                box-shadow: 0px 0px 2px rgb(0 0 0 / 64%) inset;
                opacity: 1;
                background-color: ${COLORS.background.whiteSmoke};
            }
        `,
    },
    contentDescription: {
        css: css`
            opacity: 1;
            color: ${COLORS.secondaryNavyThree};
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.light};
            font-style: normal;
            line-height: ${TYPOGRAPHY.lineHeight.smallbase};
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.small};
                        width: 100%;
                        margin-top: 10px;
                        opacity: 1;
                        color: ${COLORS.secondaryNavyThree};
                        font-weight: ${TYPOGRAPHY.weight.light};
                        font-style: normal;
                        letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                        text-align: left;
                        line-height: ${TYPOGRAPHY.lineHeight.mSmallestbase};
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 4;
                        overflow: hidden;
                        width: 100%;
                    `,
                    css`
                        opacity: 1;
                        color: ${COLORS.secondaryNavyThree};
                        font-size: ${TYPOGRAPHY.size.small};
                        font-weight: 300;
                        font-style: normal;
                        letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                        text-align: left;
                        line-height: ${TYPOGRAPHY.lineHeight.mSmallestbase};
                        width: 100%;
                        margin-top: 10px;
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 4;
                        overflow: hidden;
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                        margin-top: 10px;
                        opacity: 1;
                        color: ${COLORS.secondaryNavyThree};
                        font-size: ${TYPOGRAPHY.size.small};
                        font-weight: 300;
                        font-style: normal;
                        letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                        text-align: left;
                        line-height: ${TYPOGRAPHY.lineHeight.mSmallestbase};
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 4;
                        overflow: hidden;
                        width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imgwrapper: {
        css: css`
            position: relative;
            right: 43px;
            top: 136px;
            max-width: 630px;
            padding: 16px;
            margin-left: auto;
            border: 0.5px solid rgba(0, 0, 0, 0.5);
            /* border-top: 4px solid ${COLORS.primaryBlue}; */
            border-top: 4px solid ${getColor("primary")};
            box-shadow: 0px 2px 32px rgb(67 67 67 / 50%);
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.551109) 0%, rgba(255, 255, 255, 0.206403) 100%);
            backdrop-filter: blur(20px);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 300px !important;
                        left: 0;
                        margin: auto;
                        padding: 16px;
                        top: 205px;
                    `,
                    css`
                        max-width: 500px !important;
                        left: 0;
                        margin: auto;
                        padding: 16px;
                        top: 220px;
                    `,
                    css`
                        max-width: 500px !important;
                        left: 0;
                        margin: auto;
                        padding: 16px;
                        top: 220px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    headingWrapper: {
        variant: "h3",
        css: css`
            color: ${getColor("secondary")};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            text-transform: uppercase;
            line-height: ${TYPOGRAPHY.lineHeight.smaller};
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.larger};
                        width: 100%;
                        margin-top: 10px;
                        line-height: ${TYPOGRAPHY.lineHeight.smaller};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.larger};
                        width: 100%;
                        margin-top: 10px;
                        line-height: ${TYPOGRAPHY.lineHeight.smaller};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.larger};
                        width: 100%;
                        margin-top: 10px;
                        line-height: ${TYPOGRAPHY.lineHeight.smaller};
                    `,
                    null,
                    null,
                ])};
        `,
    },
    buttonWrapper: {
        css: css`
            max-width: 60%;
            border: 1px solid ${COLORS.normalGray};
            border-radius: 3px 20px 20px 3px;
            /* border: 0.2 solid ${COLORS.secondaryNavyOne}; */
            border: 0.2 solid ${getColor("secondary")};
            background-color: rgba(244, 244, 244, 0.500792);
            margin-top: 15px;
            height: auto;
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            text-transform: uppercase;
            position: relative;
            span {
                color: ${getColor("primary")} !important;
            }
            &:active,
            &:hover {
                &:after {
                    background-color: ${COLORS.yellow};
                }
            }
            &::after {
                content: "";
                width: 10px;
                margin-left: 10px;
                height: 10px;
                border: 1px solid;
                border-radius: 50%;
                background-color: ${COLORS.background.base};
                display: inline-block;
                color: ${COLORS.black};
                right: 14px;
                top: 14px;
                position: absolute;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        border: 1px solid ${COLORS.normalGray};
                        border-radius: 3px 20px 20px 3px;
                        background-color: rgba(244, 244, 244, 0.500792);
                        margin-top: 15px;
                        height: auto;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        text-transform: uppercase;
                        margin-bottom: 10px;
                    `,
                    css`
                        max-width: 100%;
                        border: 1px solid grey;
                        border-radius: 3px 20px 20px 3px;
                        background-color: rgba(244, 244, 244, 0.500792);
                        margin-top: 15px;
                        height: auto;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        text-transform: uppercase;
                        margin-bottom: 10px;
                    `,
                    css`
                        max-width: 100%;
                        border: 1px solid grey;
                        border-radius: 3px 20px 20px 3px;
                        background-color: rgba(244, 244, 244, 0.500792);
                        margin-top: 15px;
                        height: auto;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        text-transform: uppercase;
                        margin-bottom: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};
const defaultCustomCss = `.slideshow-wrapper {
}
.slideshow-carousel {
}
.slideshow-slide-container {
}
.slide-wrapper {
}
.slide-heading {
}
.slide-subheading {
}
.slide-button {
}
.slide-indicator {
}
.slide-dot-button {
}
.slide-arrow-wrapper {
}
.slide-arrow {
}
`;
export const TECTechnologyWidget: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);
    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }
        embla.scrollNext();
    }, [embla]);
    useRecursiveTimeout(scrollNext, 1 * 10000);
    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }
        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const links = useGetLinks(fields.slides, o => o.fields.buttonLink);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper
            css={css`
                border-radius: 7px;
                opacity: 1;
                margin-top: 50px;
                margin-bottom: 50px;
                background-color: ${COLORS.background.lighterGrayShade};
            `}
        >
            <StyledWrapper {...styles.technologyWrapper}>
                <Typography variant="h2" {...styles.headingTitle}>
                    {fields.heading}
                </Typography>
                <Typography variant="p" {...styles.description}>
                    {parse(fields.description, parserOptions)}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                    <StyledWrapper
                        css={css`
                            display: flex;
                            justify-content: flex-end;
                        `}
                    >
                        <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                                {fields.slides.map((slide, index) => {
                                    const backgroundStyle = `background-image: url(${slide.fields.image});`;
                                    const slidBannercontainer = {
                                        css: css`
                                            width: 100%;
                                            border-radius: 10px;
                                            height: 355px !important;
                                            ${backgroundStyle};
                                            background-size: cover;
                                            height: 100%;
                                            flex: 0 0 100%;
                                            position: relative;
                                            color: ${COLORS.white};
                                            background-repeat: no-repeat;
                                            ${({ theme }: { theme: BaseTheme }) =>
                                                breakpointMediaQueries(theme, [
                                                    css`
                                                        ${backgroundStyle}
                                                        height:464px !important;
                                                        background-size: cover;
                                                    `,
                                                    css`
                                                        ${backgroundStyle}
                                                        height:464px !important;
                                                        background-size: cover;
                                                    `,
                                                    css`
                                                        ${backgroundStyle}
                                                        height:464px !important;
                                                        background-size: cover;
                                                    `,
                                                    null,
                                                    null,
                                                ])}
                                        `,
                                    };
                                    return (
                                        // eslint-disable-next-line react/jsx-key
                                        <>
                                            <StyledWrapper {...slidBannercontainer} className="head">
                                                <StyledWrapper {...styles.imgwrapper} className="imageWrapper">
                                                    <Typography variant="h2" {...styles.headingWrapper}>
                                                        {parse(slide.fields.heading)}
                                                    </Typography>
                                                    <Typography variant="p" {...styles.contentDescription}>
                                                        {parse(slide.fields.subheading)}
                                                    </Typography>

                                                    {slide.fields.buttonLabel && (
                                                        <StyledWrapper>
                                                            {slide.fields.buttonType === "tecumsehButton" ? (
                                                                <StyledWrapper className="TechnologyTecumsehButton">
                                                                    <Link
                                                                        href={links[index].url}
                                                                        {...styles.linkSlidebutton}
                                                                    >
                                                                        {parse(slide.fields.buttonLabel, parserOptions)}
                                                                    </Link>
                                                                </StyledWrapper>
                                                            ) : (
                                                                <StyledWrapper className="TechnologyMasterfluxButton">
                                                                    <Link
                                                                        href={links[index].url}
                                                                        {...styles.linkSlidebutton}
                                                                    >
                                                                        {parse(slide.fields.buttonLabel, parserOptions)}
                                                                    </Link>
                                                                </StyledWrapper>
                                                            )}
                                                        </StyledWrapper>
                                                    )}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.dotsContainerWrapper}>
                        {fields.slides.map((slide, i) => {
                            // eslint-disable-next-line no-lone-blocks
                            const dotColorStyles =
                                i === selectedIndex
                                    ? `
                                    // background: white;
                                    // color: ${getColor("primary")};
                                    &:after {
                                      background-color: RGB(237, 184, 0);
                                  }
                                    
                                `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    /* ${styles.dotButton?.css || ""} */
                                    ${dotColorStyles}
                                `,
                            };
                            const dotMasterfluxStyles = {
                                css: css`
                                    /* ${styles.masterfluxButton?.css || ""} */
                                    ${dotColorStyles}
                                `,
                            };
                            return (
                                // eslint-disable-next-line react/jsx-key
                                <>
                                    <StyledWrapper>
                                        {slide.fields.buttonType === "tecumsehButton" ? (
                                            <StyledWrapper>
                                                <Button
                                                    className="TECTECHNOLOGYsidebutton"
                                                    {...dotMasterfluxStyles}
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={i}
                                                    onClick={() => embla && embla.scrollTo(i)}
                                                >
                                                    {/* {slide.fields.imgbuttonTopic} */}
                                                    {parse(slide.fields.imgbuttonTopic, parserOptions)}
                                                </Button>
                                            </StyledWrapper>
                                        ) : (
                                            <StyledWrapper>
                                                <Button
                                                    className="TechnologyMASTERFLUXSideBUTTON"
                                                    {...dotMasterfluxStyles}
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={i}
                                                    // {...dotMasterfluxStyles}
                                                    onClick={() => embla && embla.scrollTo(i)}
                                                >
                                                    {/* {slide.fields.imgbuttonTopic} */}
                                                    {parse(slide.fields.imgbuttonTopic, parserOptions)}
                                                </Button>{" "}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TECTechnologyWidget,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.heading,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "buttonType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "tecumsehButton",
                        options: [
                            {
                                displayName: "tecumsehButton",
                                value: buttonType.tecumsehButton,
                            },
                            {
                                displayName: "masterfluxButton",
                                value: buttonType.masterfluxButton,
                            },
                        ],
                    },
                    {
                        name: "imgbuttonTopic",
                        editorTemplate: "RichTextField",
                        displayName: "Image Button Topic",
                        defaultValue: "",
                    },
                    {
                        name: "heading",
                        editorTemplate: "TextField",
                        displayName: "Heading",
                        defaultValue: "",
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                    },

                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};
export default widgetModule;
